import React, { useEffect, useMemo, useState } from "react";
import { Bell, Check, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import DateField from "@/components/masterOrder/DateField";
import { Field, Input } from "@/pages/masterOrder/MOClients";
import { createOrderReminder, deleteOrderReminder, listOrderReminders, updateOrderReminder } from "@/lib/masterOrderApi";
import { formatApiError } from "@/lib/api";

const QUICK_DAYS = [14, 7, 3, 1];
const quickDayLabel = (days) => days === 1 ? "день" : days === 3 ? "дня" : "дней";
const todayISO = () => new Date().toISOString().slice(0, 10);
const subtractDays = (iso, days) => {
  if (!iso) return null;
  const date = new Date(`${iso}T12:00:00`);
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

export default function OrderReminders({ order, canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const baseDate = order.delivery?.date || order.event_date;
  const emptyForm = { action: "", reminder_date: "", reminder_time: "" };
  const [form, setForm] = useState(emptyForm);
  const ordered = useMemo(() => [...items].sort((a, b) =>
    Number(a.completed) - Number(b.completed)
    || `${a.reminder_date}T${a.reminder_time || ""}`.localeCompare(`${b.reminder_date}T${b.reminder_time || ""}`)
  ), [items]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    listOrderReminders(order.id)
      .then((result) => { if (active) setItems(result); })
      .catch((error) => toast.error(formatApiError(error)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [order.id]);

  const save = async () => {
    if (!form.action.trim()) return toast.error("Напишите напоминание");
    if (!form.reminder_date) return toast.error("Укажите дату напоминания");
    setSaving(true);
    try {
      if (editingId) {
        const updated = await updateOrderReminder(editingId, {
          action: form.action.trim(),
          reminder_date: form.reminder_date,
          reminder_time: form.reminder_time || null,
        });
        setItems((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
        toast.success("Напоминание обновлено");
      } else {
        const created = await createOrderReminder(order.id, {
          order_id: order.id,
          action: form.action.trim(),
          reminder_date: form.reminder_date,
          reminder_time: form.reminder_time || null,
          completed: false,
        });
        setItems((current) => [...current, created]);
        toast.success("Напоминание добавлено");
      }
      setForm(emptyForm);
      setAdding(false);
      setEditingId(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const startAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setAdding(true);
  };

  const startEdit = (item) => {
    setForm({
      action: item.action,
      reminder_date: item.reminder_date,
      reminder_time: item.reminder_time?.slice(0, 5) || "",
    });
    setEditingId(item.id);
    setAdding(true);
  };

  const cancelForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setAdding(false);
  };

  const remove = async (item) => {
    if (!window.confirm("Удалить это напоминание?")) return;
    try {
      await deleteOrderReminder(item.id);
      setItems((current) => current.filter((entry) => entry.id !== item.id));
      if (editingId === item.id) cancelForm();
      toast.success("Напоминание удалено");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const toggle = async (item) => {
    try {
      const updated = await updateOrderReminder(item.id, { completed: !item.completed });
      setItems((current) => current.map((entry) => entry.id === updated.id ? updated : entry));
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return (
    <section className="min-w-0 rounded-xl bg-white p-5 shadow-[inset_0_0_0_1px_#EFE4D0]" data-testid="mo-order-reminders">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell size={15} className="text-[#8B6F4B]" />
          <div className="font-['Cormorant_Garamond',serif] text-[17px] text-[#2A1D14]">Напоминания</div>
        </div>
        {canWrite && !adding && (
          <button type="button" onClick={startAdd}
            className="inline-flex min-h-9 items-center gap-1 rounded-xl px-3 text-[12px] text-[#4A3728] shadow-[inset_0_0_0_1px_#D9C6A6]">
            <Plus size={12} /> Добавить
          </button>
        )}
      </div>

      {adding && (
        <div className="mt-4 min-w-0 rounded-xl bg-[#FBF5EA] p-3 shadow-[inset_0_0_0_1px_#EFE4D0]" data-testid="mo-reminder-form">
          <Field label="Напоминание"><Input value={form.action} onChange={(value) => setForm((current) => ({ ...current, action: value }))} testid="mo-reminder-action" placeholder="Написать в Instagram и подтвердить заказ" /></Field>
          <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Быстрый выбор даты">
            {QUICK_DAYS.map((days) => (
              <button key={days} type="button" onClick={() => setForm((current) => ({ ...current, reminder_date: subtractDays(baseDate, days) || "" }))}
                className="min-h-9 rounded-xl bg-white px-3 text-[11px] text-[#5B4326] shadow-[inset_0_0_0_1px_#E0D0B8]">
                за {days} {quickDayLabel(days)}
              </button>
            ))}
            <button type="button" className="min-h-9 rounded-xl bg-white px-3 text-[11px] text-[#5B4326] shadow-[inset_0_0_0_1px_#E0D0B8]">Своя дата</button>
          </div>
          <div className="mt-3 grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Дата"><DateField value={form.reminder_date || null} onChange={(value) => setForm((current) => ({ ...current, reminder_date: value || "" }))} testid="mo-reminder-date" /></Field>
            <Field label="Время — необязательно">
              <input type="time" value={form.reminder_time} onChange={(event) => setForm((current) => ({ ...current, reminder_time: event.target.value }))}
                className="h-10 w-full rounded-lg bg-white px-3 text-[13px] text-[#2A1D14] outline-none shadow-[inset_0_0_0_1px_#EFE4D0]" data-testid="mo-reminder-time" />
            </Field>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={save} disabled={saving} className="min-h-10 rounded-xl bg-[#4A3728] px-4 text-[12px] text-[#F7EFE0] disabled:opacity-60">{saving ? "Сохраняем…" : editingId ? "Сохранить изменения" : "Сохранить"}</button>
            <button type="button" onClick={cancelForm} disabled={saving} className="min-h-10 rounded-xl bg-white px-4 text-[12px] text-[#4A3728] shadow-[inset_0_0_0_1px_#D9C6A6]">Отменить</button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {loading && <div className="text-[12px] text-[#8B6F4B]">Загружаем…</div>}
        {!loading && !ordered.length && <div className="text-[12.5px] text-[#8B6F4B]">Напоминаний пока нет.</div>}
        {ordered.map((item) => {
          const overdue = !item.completed && item.reminder_date < todayISO();
          return (
            <article key={item.id} className={`min-w-0 rounded-xl p-3 shadow-[inset_0_0_0_1px_#EFE4D0] ${item.completed ? "bg-[#F5F1E8] opacity-70" : "bg-[#FFFDF8]"}`}>
              <div className="flex min-w-0 items-start gap-3">
                {canWrite ? (
                  <button type="button" onClick={() => toggle(item)} aria-label={item.completed ? "Вернуть напоминание" : "Отметить выполненным"}
                    className={`mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full ${item.completed ? "bg-[#53604A] text-white" : "bg-white text-[#8B6F4B] shadow-[inset_0_0_0_1px_#D9C6A6]"}`}>
                    <Check size={13} />
                  </button>
                ) : <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#8B6F4B]" />}
                <div className="min-w-0 flex-1">
                  <div className={`break-words text-[13px] leading-snug text-[#2A1D14] ${item.completed ? "line-through" : ""}`}>{item.action}</div>
                  <div className={`mt-1 text-[11px] tabular-nums ${overdue ? "text-[#9B4335]" : "text-[#8B6F4B]"}`}>
                    {item.reminder_date}{item.reminder_time ? ` · ${item.reminder_time.slice(0, 5)}` : ""}{overdue ? " · просрочено" : ""}
                  </div>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 gap-1">
                    <button type="button" onClick={() => startEdit(item)} aria-label="Редактировать напоминание"
                      className="grid h-9 w-9 place-items-center rounded-xl text-[#6D5842] hover:bg-[#F7F1E7] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B6F4B]">
                      <Pencil size={14} />
                    </button>
                    <button type="button" onClick={() => remove(item)} aria-label="Удалить напоминание"
                      className="grid h-9 w-9 place-items-center rounded-xl text-[#9B4335] hover:bg-[#FAEEE9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9B4335]">
                      <Trash2 size={14} />
                    </button>
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
